import { Backdrop, Box, Button, Stack, Typography } from '@mui/material';
import { Score } from '../../api/common';
import {
  FibContentModel,
  ImageOption,
  McqMultipleContentModel,
  McqSingleContentModel,
  Question,
  TextOption,
  TfContentModel,
  TfContentModel_AnswerEnum,
} from '@protos/content_management/content.db_pb';
import { Fib } from 'libs/shared-ui/src/components/composites/Question/QuestionTypes/fib';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import { useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ExplanationHelpfulFeedback } from '../ExplanationHelpfulFeedback';
import { ReportIssueButton } from '../ReportIssueButton';
import {
  QuestionAnswerReviewBox,
  ReviewResultStatus,
} from '../QuestionAnswerReviewBox';
import { ReviewAnswerSolutionTabs } from '../ReviewAnswerSolutionTabs';
import { PalMode } from '../PalAssessmentWrapper';
import { IQuestionMetadataSolutionStep } from '../../api/learners';
import {
  AllOptionsSection,
  CorrectAnswerSection,
  DisplayAnswer,
  ReviewOption,
  SolutionSection,
  UserAnswerSection,
} from '../AnswerReviewSections';
import { formatUpiScore } from '../PerformancePopup';

// ═══════════════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════════════

type ResultType = 'correct' | 'partial' | 'incorrect';

interface MessageVariant {
  title: string;
  subtitle: string;
}

interface OverlayCopy {
  emoji: string;
  title: string;
  subtitle: string;
  bottomMessage: string;
  gradient: string;
  confidence: { emoji: string; label: string };
}

// ═══════════════════════════════════════════════════════════════════════
//  Spec §5.2 — CORRECT tiers
//  Priority: exceptional → hot → bounceBack → lightning → slow → momentum → standard
// ═══════════════════════════════════════════════════════════════════════

const CORRECT_EXCEPTIONAL_STREAK: MessageVariant[] = [
  { title: 'PAL_OVL_C_MASTERY_T', subtitle: 'PAL_OVL_C_MASTERY_S' },
  { title: 'PAL_OVL_C_FLAWLESS_T', subtitle: 'PAL_OVL_C_FLAWLESS_S' },
  { title: 'PAL_OVL_C_OUTSTANDING_T', subtitle: 'PAL_OVL_C_OUTSTANDING_S' },
];

const CORRECT_HOT_STREAK: MessageVariant[] = [
  { title: 'PAL_OVL_C_ONFIRE_T', subtitle: 'PAL_OVL_C_ONFIRE_S' },
  { title: 'PAL_OVL_C_IMPRESSIVE_T', subtitle: 'PAL_OVL_C_IMPRESSIVE_S' },
  { title: 'PAL_OVL_C_LOCKEDIN_T', subtitle: 'PAL_OVL_C_LOCKEDIN_S' },
  { title: 'PAL_OVL_C_COMMANDING_T', subtitle: 'PAL_OVL_C_COMMANDING_S' },
];

const CORRECT_BOUNCE_BACK: MessageVariant[] = [
  { title: 'PAL_OVL_C_BACKONTRACK_T', subtitle: 'PAL_OVL_C_BACKONTRACK_S' },
  { title: 'PAL_OVL_C_BREAKTHROUGH_T', subtitle: 'PAL_OVL_C_BREAKTHROUGH_S' },
  { title: 'PAL_OVL_C_TURNINGPOINT_T', subtitle: 'PAL_OVL_C_TURNINGPOINT_S' },
];

const CORRECT_LIGHTNING_FAST: MessageVariant[] = [
  { title: 'PAL_OVL_C_QUICKRECALL_T', subtitle: 'PAL_OVL_C_QUICKRECALL_S' },
  { title: 'PAL_OVL_C_INSTANT_T', subtitle: 'PAL_OVL_C_INSTANT_S' },
  { title: 'PAL_OVL_C_SHARP_T', subtitle: 'PAL_OVL_C_SHARP_S' },
];

const CORRECT_SLOW_BUT_CORRECT: MessageVariant[] = [
  { title: 'PAL_OVL_C_PERSISTENCE_T', subtitle: 'PAL_OVL_C_PERSISTENCE_S' },
  { title: 'PAL_OVL_C_FIGUREDOUT_T', subtitle: 'PAL_OVL_C_FIGUREDOUT_S' },
  { title: 'PAL_OVL_C_HARDEARNED_T', subtitle: 'PAL_OVL_C_HARDEARNED_S' },
];

const CORRECT_BUILDING_MOMENTUM: MessageVariant[] = [
  {
    title: 'PAL_OVL_C_GAININGMOMENTUM_T',
    subtitle: 'PAL_OVL_C_GAININGMOMENTUM_S',
  },
  { title: 'PAL_OVL_C_CONSISTENT_T', subtitle: 'PAL_OVL_C_CONSISTENT_S' },
  { title: 'PAL_OVL_C_INTHEZONE_T', subtitle: 'PAL_OVL_C_INTHEZONE_S' },
  { title: 'PAL_OVL_C_SOLIDSTREAK_T', subtitle: 'PAL_OVL_C_SOLIDSTREAK_S' },
];

const CORRECT_STANDARD: MessageVariant[] = [
  { title: 'PAL_OVL_C_CORRECT_T', subtitle: 'PAL_OVL_C_CORRECT_S' },
  { title: 'PAL_OVL_C_WELLDONE_T', subtitle: 'PAL_OVL_C_WELLDONE_S' },
  { title: 'PAL_OVL_C_GOTIT_T', subtitle: 'PAL_OVL_C_GOTIT_S' },
  { title: 'PAL_OVL_C_RIGHTANSWER_T', subtitle: 'PAL_OVL_C_RIGHTANSWER_S' },
  { title: 'PAL_OVL_C_GOODTHINKING_T', subtitle: 'PAL_OVL_C_GOODTHINKING_S' },
  { title: 'PAL_OVL_C_SPOTON_T', subtitle: 'PAL_OVL_C_SPOTON_S' },
];

// ═══════════════════════════════════════════════════════════════════════
//  Spec §5.3 — PARTIAL tiers
// ═══════════════════════════════════════════════════════════════════════

const PARTIAL_RUSHED: MessageVariant[] = [
  { title: 'PAL_OVL_P_TOOQUICK_T', subtitle: 'PAL_OVL_P_TOOQUICK_S' },
  { title: 'PAL_OVL_P_MISSEDSOME_T', subtitle: 'PAL_OVL_P_MISSEDSOME_S' },
];

const PARTIAL_CLOSE: MessageVariant[] = [
  { title: 'PAL_OVL_P_ALMOSTTHERE_T', subtitle: 'PAL_OVL_P_ALMOSTTHERE_S' },
  {
    title: 'PAL_OVL_P_NEARLYCOMPLETE_T',
    subtitle: 'PAL_OVL_P_NEARLYCOMPLETE_S',
  },
  { title: 'PAL_OVL_P_CLOSECALL_T', subtitle: 'PAL_OVL_P_CLOSECALL_S' },
  { title: 'PAL_OVL_P_MOSTOFTHEWAY_T', subtitle: 'PAL_OVL_P_MOSTOFTHEWAY_S' },
];

const PARTIAL_NEEDS_WORK: MessageVariant[] = [
  { title: 'PAL_OVL_P_PARTIALCREDIT_T', subtitle: 'PAL_OVL_P_PARTIALCREDIT_S' },
  { title: 'PAL_OVL_P_HALFWAYTHERE_T', subtitle: 'PAL_OVL_P_HALFWAYTHERE_S' },
  { title: 'PAL_OVL_P_MIXEDRESULTS_T', subtitle: 'PAL_OVL_P_MIXEDRESULTS_S' },
];

// ═══════════════════════════════════════════════════════════════════════
//  Spec §5.4 — INCORRECT tiers
// ═══════════════════════════════════════════════════════════════════════

const INCORRECT_REPEAT_MISS: MessageVariant[] = [
  { title: 'PAL_OVL_I_STILLTRICKY_T', subtitle: 'PAL_OVL_I_STILLTRICKY_S' },
  { title: 'PAL_OVL_I_TOUGHSTRETCH_T', subtitle: 'PAL_OVL_I_TOUGHSTRETCH_S' },
  { title: 'PAL_OVL_I_KEEPATIT_T', subtitle: 'PAL_OVL_I_KEEPATIT_S' },
];

const INCORRECT_RUSHED_WRONG: MessageVariant[] = [
  { title: 'PAL_OVL_I_TOOFAST_T', subtitle: 'PAL_OVL_I_TOOFAST_S' },
  { title: 'PAL_OVL_I_SLOWDOWN_T', subtitle: 'PAL_OVL_I_SLOWDOWN_S' },
];

const INCORRECT_SLOW_MISS: MessageVariant[] = [
  {
    title: 'PAL_OVL_I_TOUGHQUESTION_T',
    subtitle: 'PAL_OVL_I_TOUGHQUESTION_S',
  },
  { title: 'PAL_OVL_I_NEEDSREVIEW_T', subtitle: 'PAL_OVL_I_NEEDSREVIEW_S' },
];

const INCORRECT_FIRST_MISS: MessageVariant[] = [
  { title: 'PAL_OVL_I_NOTQUITE_T', subtitle: 'PAL_OVL_I_NOTQUITE_S' },
  { title: 'PAL_OVL_I_MISSEDTHIS_T', subtitle: 'PAL_OVL_I_MISSEDTHIS_S' },
  { title: 'PAL_OVL_I_WRONGANSWER_T', subtitle: 'PAL_OVL_I_WRONGANSWER_S' },
  { title: 'PAL_OVL_I_OFFTRACK_T', subtitle: 'PAL_OVL_I_OFFTRACK_S' },
  { title: 'PAL_OVL_I_NOTTHISTIME_T', subtitle: 'PAL_OVL_I_NOTTHISTIME_S' },
];

const TIMEOUT_MESSAGE: MessageVariant = {
  title: 'PAL_OVL_TIMEOUT_T',
  subtitle: 'PAL_OVL_TIMEOUT_S',
};

// ═══════════════════════════════════════════════════════════════════════
//  Spec §6 — Confidence Display (relative to idealMaxTime)
// ═══════════════════════════════════════════════════════════════════════

type ConfidenceBracket = { maxTime: number; emoji: string; label: string };

const DEFAULT_IDEAL_MAX = 120;

function buildConfidenceTable(
  resultType: ResultType,
  idealMax: number
): ConfidenceBracket[] {
  if (resultType === 'correct') {
    return [
      {
        maxTime: idealMax * 0.08,
        emoji: '😎',
        label: 'PAL_OVL_CONF_INSTANT_RECALL',
      },
      { maxTime: idealMax * 0.25, emoji: '🎯', label: 'PAL_OVL_CONF_HIGH' },
      { maxTime: idealMax * 0.5, emoji: '💪', label: 'PAL_OVL_CONF_CONFIDENT' },
      {
        maxTime: idealMax * 0.75,
        emoji: '🤔',
        label: 'PAL_OVL_CONF_WORKED_THROUGH',
      },
      { maxTime: Infinity, emoji: '😅', label: 'PAL_OVL_CONF_HARD_EARNED' },
    ];
  }
  if (resultType === 'partial') {
    return [
      {
        maxTime: idealMax * 0.25,
        emoji: '⚡',
        label: 'PAL_OVL_CONF_QUICK_INCOMPLETE',
      },
      {
        maxTime: idealMax * 0.5,
        emoji: '🔍',
        label: 'PAL_OVL_CONF_PARTIALLY',
      },
      {
        maxTime: Infinity,
        emoji: '📖',
        label: 'PAL_OVL_CONF_NEEDS_REINFORCE',
      },
    ];
  }
  return [
    {
      maxTime: idealMax * 0.125,
      emoji: '⚡',
      label: 'PAL_OVL_CONF_TOO_RUSHED',
    },
    {
      maxTime: idealMax * 0.5,
      emoji: '💭',
      label: 'PAL_OVL_CONF_MISUNDERSTOOD',
    },
    { maxTime: Infinity, emoji: '📚', label: 'PAL_OVL_CONF_CONCEPT_GAP' },
  ];
}

// ═══════════════════════════════════════════════════════════════════════
//  Spec §7 — Bottom Messages
// ═══════════════════════════════════════════════════════════════════════

const BOTTOM_CORRECT: [string[], string[], string[]] = [
  [
    'PAL_OVL_BOTTOM_LOADING_NEXT',
    'PAL_OVL_BOTTOM_MOVING_ON',
    'PAL_OVL_BOTTOM_KEEP_GOING',
  ],
  [
    'PAL_OVL_BOTTOM_KEEP_STREAK',
    'PAL_OVL_BOTTOM_ANOTHER',
    'PAL_OVL_BOTTOM_MOMENTUM',
  ],
  [
    'PAL_OVL_BOTTOM_RAISING_BAR',
    'PAL_OVL_BOTTOM_EARNED_HARDER',
    'PAL_OVL_BOTTOM_CHALLENGE_INCOMING',
  ],
];

const BOTTOM_PARTIAL = [
  'PAL_OVL_BOTTOM_STRENGTHEN',
  'PAL_OVL_BOTTOM_SIMILAR',
  'PAL_OVL_BOTTOM_BUILDING',
];

const BOTTOM_INCORRECT = [
  'PAL_OVL_BOTTOM_DIFFERENT_ANGLE',
  'PAL_OVL_BOTTOM_FRESH_START',
];

// ═══════════════════════════════════════════════════════════════════════
//  Spec §4 — Top Icons & Gradients
// ═══════════════════════════════════════════════════════════════════════

const GRADIENTS: Record<ResultType | 'timeout', string> = {
  correct: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
  partial: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  incorrect: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
  timeout: 'linear-gradient(135deg, #334155 0%, #64748b 100%)',
};

const TOP_ICONS: Record<ResultType, string> = {
  correct: '✓',
  partial: '◐',
  incorrect: '✗',
};

// Spec §9 — particle counts per result type
const PARTICLE_COUNTS: Record<ResultType, number> = {
  correct: 20,
  partial: 8,
  incorrect: 6,
};

// ═══════════════════════════════════════════════════════════════════════
//  Selection Logic
// ═══════════════════════════════════════════════════════════════════════

function pickRandom<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

function resolveResultType(
  score: number | undefined,
  isTimedOut?: boolean
): ResultType {
  if (isTimedOut || score === 0) return 'incorrect';
  if (score === 1) return 'correct';
  return 'partial';
}

function resolveReviewBoxStatus(
  score: number | undefined,
  isTimedOut?: boolean,
  isSkipped?: boolean
): ReviewResultStatus {
  if (isTimedOut) return 'timeout';
  if (isSkipped) return 'skipped';
  return resolveResultType(score);
}

export { resolveResultType as resolveReviewResultType };

/**
 * Spec §5.1 — first matching tier wins (priority order).
 *
 * All time thresholds are expressed as fractions of `idealMax`:
 *   lightning-fast  ≤ 8%   of idealMax
 *   slow           ≥ 75%   of idealMax
 *
 * `isFirstQuestion` (derived from questioNumber === 1) means streak===1 after
 * the very first answer — no prior errors exist, so bounce-back doesn't apply.
 */
function selectCorrectTier(
  streak: number,
  timeTaken: number,
  isFirstQuestion: boolean,
  idealMax: number
): MessageVariant[] {
  if (streak >= 7) return CORRECT_EXCEPTIONAL_STREAK;
  if (streak >= 4) return CORRECT_HOT_STREAK;
  if (!isFirstQuestion && streak === 1) return CORRECT_BOUNCE_BACK;
  if (timeTaken <= idealMax * 0.08 && streak <= 3)
    return CORRECT_LIGHTNING_FAST;
  if (timeTaken >= idealMax * 0.75) return CORRECT_SLOW_BUT_CORRECT;
  if (streak >= 2) return CORRECT_BUILDING_MOMENTUM;
  return CORRECT_STANDARD;
}

function selectPartialTier(
  timeTaken: number,
  score: number,
  idealMax: number
): MessageVariant[] {
  if (timeTaken <= idealMax * 0.17) return PARTIAL_RUSHED;
  if (score > 0.5) return PARTIAL_CLOSE;
  return PARTIAL_NEEDS_WORK;
}

function selectIncorrectTier(
  consecutiveErrors: number,
  timeTaken: number,
  idealMax: number
): MessageVariant[] {
  if (consecutiveErrors >= 2) return INCORRECT_REPEAT_MISS;
  if (timeTaken <= idealMax * 0.125) return INCORRECT_RUSHED_WRONG;
  if (timeTaken >= idealMax * 0.75) return INCORRECT_SLOW_MISS;
  return INCORRECT_FIRST_MISS;
}

function getConfidence(
  resultType: ResultType,
  timeTaken: number,
  idealMax: number
): { emoji: string; label: string } {
  const table = buildConfidenceTable(resultType, idealMax);
  return table.find((b) => timeTaken <= b.maxTime) ?? table[table.length - 1];
}

function getBottomMessage(resultType: ResultType, streak: number): string {
  if (resultType === 'correct') {
    const pool =
      streak >= 4
        ? BOTTOM_CORRECT[2]
        : streak >= 2
        ? BOTTOM_CORRECT[1]
        : BOTTOM_CORRECT[0];
    return pickRandom(pool);
  }
  return pickRandom(
    resultType === 'partial' ? BOTTOM_PARTIAL : BOTTOM_INCORRECT
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Main Copy Builder
// ═══════════════════════════════════════════════════════════════════════

export function buildOverlayCopy(params: {
  score: number | undefined;
  streak: number;
  timeTaken: number;
  idealMaxTime: number;
  isTimedOut?: boolean;
  isFirstQuestion: boolean;
}): OverlayCopy {
  const {
    score,
    streak,
    timeTaken,
    idealMaxTime,
    isTimedOut,
    isFirstQuestion,
  } = params;
  const resultType = resolveResultType(score, isTimedOut);
  const absStreak = Math.abs(streak);

  let tier: MessageVariant[];
  if (isTimedOut) {
    tier = [TIMEOUT_MESSAGE];
  } else if (resultType === 'correct') {
    tier = selectCorrectTier(streak, timeTaken, isFirstQuestion, idealMaxTime);
  } else if (resultType === 'partial') {
    tier = selectPartialTier(timeTaken, score ?? 0, idealMaxTime);
  } else {
    tier = selectIncorrectTier(absStreak, timeTaken, idealMaxTime);
  }

  const variant = pickRandom(tier);

  return {
    emoji: isTimedOut ? '⏳' : TOP_ICONS[resultType],
    title: variant.title,
    subtitle: variant.subtitle,
    bottomMessage: getBottomMessage(resultType, streak),
    gradient: isTimedOut ? GRADIENTS.timeout : GRADIENTS[resultType],
    confidence: getConfidence(resultType, timeTaken, idealMaxTime),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Static Styles (hoisted for perf — no re-creation per render)
// ═══════════════════════════════════════════════════════════════════════

const REVIEW_CARD_WIDTH = 600;
const REVIEW_PANEL_BG = '#f8f9fa';

const glassBoxSx = {
  bgcolor: 'rgba(255,255,255,0.18)',
  border: '1px solid rgba(255,255,255,0.28)',
  borderRadius: 2.5,
  px: 2,
  py: 1,
} as const;

const statBoxSx = {
  ...glassBoxSx,
  textAlign: 'center' as const,
  minWidth: 72,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
} as const;

const statValueSx = {
  fontSize: 20,
  fontWeight: 700,
  color: 'white',
  lineHeight: 1.4,
} as const;

const statLabelSx = {
  fontSize: 9,
  fontWeight: 700,
  color: 'rgba(255,255,255,0.85)',
  letterSpacing: 0.8,
  textTransform: 'uppercase' as const,
  mt: 0.25,
} as const;

// ═══════════════════════════════════════════════════════════════════════
//  Utility Functions
// ═══════════════════════════════════════════════════════════════════════

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const formatAccuracy = (a: number) => `${Math.round(a * 100)}%`;

const formatXpGain = (xp: number) => {
  const val = formatUpiScore(xp || 0);
  return xp > 0 ? `+${val}` : `${val}`;
};

// ═══════════════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════════════

interface IPalAnswerOverlayProps {
  open: boolean;
  onContinue: () => void;
  score?: number;
  streak?: number;
  userSubmittedAnswer?: string[];
  correctAnswer?: Score['correct_answer'];
  question?: Question;
  accuracy?: number;
  xpGain?: number;
  timeTakenInSeconds?: number;
  idealMaxTimeInSeconds?: number;
  isTimedOut?: boolean;
  isSkipped?: boolean;
  palMode?: PalMode;
  solution: IQuestionMetadataSolutionStep[];
  questioNumber: number;
  conceptLabel?: string;
  conceptEmoji?: string;
  sessionId?: string;
  questionId?: string;
  summaryText?: string;
}

export const PalAnswerOverlay = ({
  open,
  onContinue,
  score,
  streak = 0,
  userSubmittedAnswer,
  correctAnswer,
  question,
  accuracy,
  xpGain,
  timeTakenInSeconds,
  idealMaxTimeInSeconds,
  isTimedOut,
  isSkipped,
  solution,
  questioNumber,
  conceptLabel,
  conceptEmoji,
  sessionId,
  questionId,
}: IPalAnswerOverlayProps) => {
  const { t } = useTranslation();
  const isFirstQuestion = questioNumber === 1;
  const interpolationValues = {
    streak: Math.abs(streak),
    timeTaken: timeTakenInSeconds ?? 0,
  };

  const userSubmittedAnswers =
    question && userSubmittedAnswer
      ? getUserSubmittedDisplayAnswer(question, userSubmittedAnswer, score)
      : [];

  const correctAnswers =
    question && correctAnswer
      ? getCorrectDisplayAnswer(question, correctAnswer)
      : [];

  const allReviewOptions = question
    ? getAllReviewOptions(question, userSubmittedAnswer ?? [], correctAnswer)
    : [];

  const showAllOptions = allReviewOptions.length > 0;

  const resultType = resolveResultType(score, isTimedOut);
  const reviewResultStatus = resolveReviewBoxStatus(
    score,
    isTimedOut,
    isSkipped
  );
  const particleCount = PARTICLE_COUNTS[resultType];

  const transitionParticles = useMemo(
    () =>
      Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 8 + Math.random() * 14,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
      })),
    [score, question, particleCount]
  );

  const [transitionProgressPercent, setTransitionProgressPercent] = useState(0);

  const idealMax = idealMaxTimeInSeconds ?? DEFAULT_IDEAL_MAX;

  const overlayCopy = useMemo(
    () =>
      buildOverlayCopy({
        score,
        streak,
        timeTaken: timeTakenInSeconds ?? 0,
        idealMaxTime: idealMax,
        isTimedOut,
        isFirstQuestion,
      }),
    [score, streak, timeTakenInSeconds, idealMax, isTimedOut, isFirstQuestion]
  );

  useEffect(() => {
    setTransitionProgressPercent(0);
    const id = setTimeout(() => setTransitionProgressPercent(100), 300);
    return () => clearTimeout(id);
  }, [question, score]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: 1300,
        bgcolor: 'transparent',
        position: 'fixed',
        inset: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: overlayCopy.gradient,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarGutter: 'stable',
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {transitionParticles.map((p) => (
            <Box
              key={p.id}
              sx={{
                position: 'absolute',
                left: `${p.left}%`,
                top: `${p.top}%`,
                width: p.size,
                height: p.size,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.25)',
                animation: 'transitionParticleFloat 4s ease-in-out infinite',
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                pointerEvents: 'none',
                '@keyframes transitionParticleFloat': {
                  '0%, 100%': { transform: 'translateY(0)', opacity: 0.1 },
                  '50%': { transform: 'translateY(-25px)', opacity: 0.2 },
                },
              }}
            />
          ))}

          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 3, md: 5 },
              px: { xs: 2, sm: 3, md: 5 },
              py: { xs: 3, md: 0 },
              width: '100%',
              maxWidth: 1280,
              mx: 'auto',
              boxSizing: 'border-box',
            }}
          >
            {/* Left column */}
            <Box
              sx={{
                flex: { xs: 'none', md: '0 1 420px' },
                maxWidth: { xs: 420, md: 420 },
                width: '100%',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box sx={{ fontSize: 48, mb: 1.5, lineHeight: 1 }}>
                {overlayCopy.emoji}
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: 26, sm: 32, md: 36 },
                  fontWeight: 800,
                  color: 'white',
                  mb: 0.5,
                  letterSpacing: -0.5,
                  lineHeight: 1.15,
                }}
              >
                {t(overlayCopy.title, interpolationValues)}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 14, md: 15 },
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.92)',
                  mb: '10px',
                  maxWidth: 340,
                }}
              >
                {t(overlayCopy.subtitle, interpolationValues)}
              </Typography>

              <Stack
                direction="row"
                spacing={1.5}
                justifyContent="center"
                sx={{ mb: '16px' }}
              >
                {timeTakenInSeconds !== undefined && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                      gap: '5px',
                      ...glassBoxSx,
                    }}
                  >
                    <Box sx={{ fontSize: 18, lineHeight: 1 }}>⏱️</Box>
                    <Typography sx={statValueSx}>
                      {formatTime(timeTakenInSeconds)}
                    </Typography>
                    <Typography sx={statLabelSx}>
                      {t('PAL_OVL_TIME_TAKEN')}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ...glassBoxSx,
                    flexDirection: 'column',
                    textAlign: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ fontSize: 18, lineHeight: 1 }}>
                    {overlayCopy.confidence.emoji}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: 'white',
                      letterSpacing: 0.3,
                    }}
                  >
                    {t(overlayCopy.confidence.label)}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  height: 5,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderRadius: 100,
                  overflow: 'hidden',
                  mb: '12px',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${transitionProgressPercent}%`,
                    bgcolor: 'white',
                    borderRadius: 100,
                    transition: 'width 0.8s ease-out',
                  }}
                />
              </Box>

              <Stack
                direction="row"
                spacing={1.25}
                justifyContent="center"
                sx={{ mb: '16px' }}
              >
                {streak !== undefined && streak > 0 && (
                  <Box sx={statBoxSx}>
                    <Typography sx={statValueSx}>{streak}</Typography>
                    <Typography sx={statLabelSx}>
                      {t('PAL_OVL_STREAK')}
                    </Typography>
                  </Box>
                )}
                <Box sx={statBoxSx}>
                  <Typography sx={statValueSx}>
                    {accuracy !== undefined ? formatAccuracy(accuracy) : '—'}
                  </Typography>
                  <Typography sx={statLabelSx}>
                    {t('PAL_OVL_ACCURACY')}
                  </Typography>
                </Box>
                {xpGain !== undefined && xpGain !== 0 && (
                  <Box sx={statBoxSx}>
                    <Typography sx={statValueSx}>
                      {formatXpGain(xpGain)}
                    </Typography>
                    <Typography sx={statLabelSx}>
                      {t('PAL_OVL_MASTERY_SCORE')}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {overlayCopy.bottomMessage && (
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    borderRadius: 100,
                    py: 1,
                    px: 3,
                    mb: '16px',
                    width: '100%',
                    maxWidth: 340,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.92)',
                      textAlign: 'center',
                    }}
                  >
                    {t(overlayCopy.bottomMessage)}
                  </Typography>
                </Box>
              )}

              <Button
                onClick={onContinue}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.25,
                  py: 1.5,
                  px: 4,
                  bgcolor: 'white',
                  color: '#1a1a2e',
                  borderRadius: 100,
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    bgcolor: 'white',
                  },
                }}
              >
                <span>{t('PAL_OVL_GOT_IT_CONTINUE')}</span>
                <span style={{ fontSize: 18, marginLeft: 4 }}>→</span>
              </Button>
            </Box>

            {/* Right: review card */}
            <Box
              sx={{
                flex: { xs: 'none', md: `0 1 ${REVIEW_CARD_WIDTH}px` },
                maxWidth: { xs: '100%', md: REVIEW_CARD_WIDTH },
                width: '100%',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                bgcolor: REVIEW_PANEL_BG,
                maxHeight: '70vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  bgcolor: REVIEW_PANEL_BG,
                  px: '16px',
                  py: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>
                  {reviewResultStatus === 'timeout'
                    ? '⏳'
                    : reviewResultStatus === 'skipped'
                    ? '⏭'
                    : score == 1
                    ? '✨'
                    : '🤔'}
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: 16, md: 17 },
                    fontWeight: 700,
                    color: '#1e293b',
                    lineHeight: 1.35,
                  }}
                >
                  {reviewResultStatus === 'timeout'
                    ? t('PAL_OVL_REVIEW_TITLE_TIMEOUT')
                    : reviewResultStatus === 'skipped'
                    ? t('PAL_OVL_REVIEW_TITLE_SKIPPED')
                    : score == 1
                    ? t('PAL_OVL_REVIEW_TITLE_CORRECT')
                    : t('PAL_OVL_REVIEW_TITLE_WRONG')}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarGutter: 'stable',
                  px: '16px',
                  pb: '12px',
                  bgcolor: REVIEW_PANEL_BG,
                }}
              >
                <QuestionAnswerReviewBox
                  questionNumber={questioNumber}
                  resultStatus={reviewResultStatus}
                  question={question}
                  conceptLabel={conceptLabel}
                  conceptEmoji={conceptEmoji}
                >
                  <ReviewAnswerSolutionTabs
                    resetKey={question?.questionId}
                    hasSolution={solution.length > 0}
                    answer={
                      showAllOptions ? (
                        <AllOptionsSection
                          options={allReviewOptions}
                          embedded
                        />
                      ) : (
                        <>
                          <UserAnswerSection
                            answers={userSubmittedAnswers}
                            embedded
                          />
                          {score !== 1 && (
                            <CorrectAnswerSection
                              correctAnswers={correctAnswers}
                              userSubmittedAnswers={userSubmittedAnswers}
                              embedded
                            />
                          )}
                        </>
                      )
                    }
                    solution={<SolutionSection solution={solution} />}
                  />
                </QuestionAnswerReviewBox>
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  px: '16px',
                  py: '12px',
                  borderTop: '1px dashed #e2e8f0',
                  bgcolor: REVIEW_PANEL_BG,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  gap: 1.5,
                }}
              >
                {sessionId && questionId && (
                  <ExplanationHelpfulFeedback
                    sessionId={sessionId}
                    questionId={questionId}
                  />
                )}
                <Box
                  sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    bgcolor: '#e2e8f0',
                    flexShrink: 0,
                    display: { xs: 'none', sm: 'block' },
                    minHeight: 28,
                  }}
                />
                {sessionId && questionId && (
                  <ReportIssueButton
                    sessionId={sessionId}
                    questionId={questionId}
                    questionNumber={questioNumber}
                    conceptName={conceptLabel}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Backdrop>
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  Display Answer Helpers
// ═══════════════════════════════════════════════════════════════════════

const getOptionExplanation = (
  option: McqSingleContentModel['options'][number] | undefined
): string | undefined => {
  if (option?.optionType.case === 'textOption') {
    return option.optionType.value.correctIncorrectExplanationText;
  }
  if (option?.optionType.case === 'imageOption') {
    return option.optionType.value.correctIncorrectExplanationText;
  }
  return undefined;
};

const getCorrectOptionKeys = (
  question: Question,
  correctAnswer?: Score['correct_answer']
): Set<string> => {
  const candidates = normalizeCorrectAnswerCandidates(question, correctAnswer);
  return new Set(candidates[0] ?? []);
};

export function getAllReviewOptions(
  question: Question,
  userSubmittedAnswer: string[],
  correctAnswer?: Score['correct_answer']
): ReviewOption[] {
  const questionValue = question.question?.model?.value;
  const correctKeys = getCorrectOptionKeys(question, correctAnswer);
  const selectedKeys = new Set(userSubmittedAnswer);

  const resolveStatus = (optionKey: string): ReviewOption['status'] => {
    const isCorrect = correctKeys.has(optionKey);
    const isSelected = selectedKeys.has(optionKey);

    if (isSelected && isCorrect) return 'userCorrect';
    if (isSelected && !isCorrect) return 'userWrong';
    if (!isSelected && isCorrect) return 'missed';
    return 'neutral';
  };

  if (
    questionValue instanceof McqSingleContentModel ||
    questionValue instanceof McqMultipleContentModel
  ) {
    return questionValue.options.map((option, index) => {
      const optionKey = String(index + 1);
      const status = resolveStatus(optionKey);
      const isCorrect = correctKeys.has(optionKey);

      return {
        answer: buildDisplayAnswerFromOption(
          option,
          isCorrect
            ? QAttemptResultEnum.RESPONSE_CORRECT
            : QAttemptResultEnum.RESPONSE_INCORRECT
        ),
        status,
        feedback: getOptionExplanation(option),
      };
    });
  }

  if (questionValue instanceof TfContentModel) {
    const tfOptions: Array<{
      labelKey: 'PAL_AR_TF_TRUE' | 'PAL_AR_TF_FALSE';
      enumVal: TfContentModel_AnswerEnum;
    }> = [
      {
        labelKey: 'PAL_AR_TF_TRUE',
        enumVal: TfContentModel_AnswerEnum.TF_ANSWER_T,
      },
      {
        labelKey: 'PAL_AR_TF_FALSE',
        enumVal: TfContentModel_AnswerEnum.TF_ANSWER_F,
      },
    ];

    return tfOptions.map(({ labelKey, enumVal }) => {
      const isCorrect = questionValue.correct === enumVal;
      const isSelected = userSubmittedAnswer.some(
        (answer) => normalizeTfAnswerToEnum(answer) === enumVal
      );

      let status: ReviewOption['status'] = 'neutral';
      if (isSelected && isCorrect) status = 'userCorrect';
      else if (isSelected && !isCorrect) status = 'userWrong';
      else if (!isSelected && isCorrect) status = 'missed';

      return {
        answer: {
          text: new TextOption({ optionText: i18n.t(labelKey) }),
          isCorrect: isCorrect
            ? QAttemptResultEnum.RESPONSE_CORRECT
            : QAttemptResultEnum.RESPONSE_INCORRECT,
        },
        status,
      };
    });
  }

  return [];
}

const normalizeTfAnswerToEnum = (answer: string): TfContentModel_AnswerEnum => {
  if (answer == '2' || answer == 'TF_ANSWER_F')
    return TfContentModel_AnswerEnum.TF_ANSWER_F;
  if (answer == '1' || answer == 'TF_ANSWER_T')
    return TfContentModel_AnswerEnum.TF_ANSWER_T;
  return TfContentModel_AnswerEnum.TF_ANSWER_UNDEFINED;
};

const buildDisplayAnswerFromOption = (
  option: McqSingleContentModel['options'][number] | undefined,
  correctness: QAttemptResultEnum
): DisplayAnswer => {
  if (option?.optionType.case == 'textOption') {
    return { text: option.optionType.value, isCorrect: correctness };
  }
  if (option?.optionType.case == 'imageOption') {
    return { image: option.optionType.value, isCorrect: correctness };
  }
  return { text: undefined, image: undefined, isCorrect: correctness };
};

const mapMcqAnswersToDisplay = (
  options: McqSingleContentModel['options'],
  userSubmittedAnswer: string[],
  isCorrectAnswer: (answer: string) => boolean
): DisplayAnswer[] =>
  userSubmittedAnswer.map((answer) => {
    const option = options[Number(answer) - 1];
    const correctness = isCorrectAnswer(answer)
      ? QAttemptResultEnum.RESPONSE_CORRECT
      : QAttemptResultEnum.RESPONSE_INCORRECT;
    return buildDisplayAnswerFromOption(option, correctness);
  });

const toTfDisplayAnswer = (
  answer: string,
  correctAnswer: TfContentModel_AnswerEnum
): DisplayAnswer => {
  const normalized = normalizeTfAnswerToEnum(answer);
  return {
    text:
      normalized === TfContentModel_AnswerEnum.TF_ANSWER_T
        ? new TextOption({ optionText: i18n.t('PAL_AR_TF_TRUE') })
        : normalized === TfContentModel_AnswerEnum.TF_ANSWER_F
        ? new TextOption({ optionText: i18n.t('PAL_AR_TF_FALSE') })
        : undefined,
    isCorrect:
      normalized == correctAnswer
        ? QAttemptResultEnum.RESPONSE_CORRECT
        : QAttemptResultEnum.RESPONSE_INCORRECT,
  };
};

const normalizeCorrectAnswerCandidates = (
  question: Question,
  answer?: Score['correct_answer']
): string[][] => {
  const questionValue = question.question?.model?.value;

  if (questionValue instanceof McqSingleContentModel) {
    const candidate =
      typeof answer === 'string' || typeof answer === 'number'
        ? String(answer)
        : questionValue.correct;
    return [[candidate]];
  }

  if (questionValue instanceof McqMultipleContentModel) {
    const candidate =
      Array.isArray(answer) && !Array.isArray(answer[0])
        ? answer.flat()
        : questionValue.correct;
    return [candidate];
  }

  if (questionValue instanceof TfContentModel) {
    const candidate =
      typeof answer === 'string' || typeof answer === 'number'
        ? String(answer)
        : questionValue.correct.toString();
    return [[candidate]];
  }

  if (questionValue instanceof FibContentModel) {
    if (Array.isArray(answer) && Array.isArray(answer[0])) {
      return answer.map((val) => (Array.isArray(val) ? val : [val]));
    }

    const correctInfo = questionValue.correctAnswerInfo;
    const groupedByBlank: string[][] = [];
    correctInfo.forEach(
      (corr) => (groupedByBlank[corr.blankPosition] = corr.correct)
    );

    const maxLength = Math.max(
      ...groupedByBlank.map((subArr) => subArr.length)
    );
    const transposed = Array(maxLength)
      .fill(0)
      .map(() => Array(groupedByBlank.length).fill(''));

    for (let i = 0; i < groupedByBlank.length; i++) {
      for (let j = 0; j < maxLength; j++) {
        transposed[j][i] = groupedByBlank[i][j];
      }
    }

    return transposed;
  }

  return [];
};

export function getUserSubmittedDisplayAnswer(
  question: Question,
  userSubmittedAnswer: string[],
  responseScore?: number
): DisplayAnswer[] {
  const questionValue = question.question?.model?.value;

  if (questionValue instanceof McqSingleContentModel) {
    return mapMcqAnswersToDisplay(
      questionValue.options,
      userSubmittedAnswer,
      (answer) => questionValue.correct == answer
    );
  }

  if (questionValue instanceof McqMultipleContentModel) {
    return mapMcqAnswersToDisplay(
      questionValue.options,
      userSubmittedAnswer,
      (answer) => questionValue.correct.includes(answer)
    );
  }

  if (questionValue instanceof TfContentModel) {
    return userSubmittedAnswer.map((answer) =>
      toTfDisplayAnswer(answer, questionValue.correct)
    );
  }

  if (questionValue instanceof FibContentModel) {
    const fibQues = new Fib(question);
    const correct = fibQues.isCorrect(userSubmittedAnswer);
    return [
      {
        text: new TextOption({ optionText: userSubmittedAnswer.join(', ') }),
        isCorrect:
          responseScore !== undefined
            ? responseScore == 0
              ? QAttemptResultEnum.RESPONSE_INCORRECT
              : responseScore == 1
              ? QAttemptResultEnum.RESPONSE_CORRECT
              : QAttemptResultEnum.RESPONSE_PARTIALLY_CORRECT
            : correct,
      },
    ];
  }

  return [];
}

export const getCorrectDisplayAnswer = (
  question: Question,
  answer: Score['correct_answer']
) =>
  normalizeCorrectAnswerCandidates(question, answer).map((candidate) =>
    getUserSubmittedDisplayAnswer(question, candidate, 1)
  );
